<template>
    <v-card id="issue-details">
        <v-card-title>
            <v-icon v-if="isNew">mdi-sticker-plus</v-icon>
            <v-icon v-if="isEdit">mdi-sticker</v-icon>
            <v-icon v-if="isEditTemplate || isNewTemplate">mdi-note-multiple</v-icon>
            <v-icon v-if="isEditRecurring || isNewRecurring">mdi-calendar-edit</v-icon>
            <span class="ml-3" v-if="isNew">Create Issue</span>
            <span class="ml-3" v-if="isEdit">Issue Details</span>
            <span class="ml-3" v-if="isEditTemplate">Issue Template</span>
            <span class="ml-3" v-if="isNewTemplate">Create Issue Template</span>
            <span class="ml-3" v-if="isEditRecurring">Edit Recurring Issue</span>
            <span class="ml-3" v-if="isNewRecurring">New Recurring Issue</span>

            <v-spacer />
            <v-btn text @click="showSitebook = !showSitebook" v-if="sitebook && !showSitebook">{{ `sitebook - ${sitebook.site.site_location}` }}</v-btn>
            <v-btn text @click="showSitebook = !showSitebook" v-if="sitebook && showSitebook">{{ `Back to issue` }}</v-btn>
        </v-card-title>

        <v-divider v-if="!showSitebook" />

        <v-tabs v-model="tab" color="#ffffff" v-if="!showSitebook">
            <v-tabs-slider color="#ffffff"></v-tabs-slider>
            <v-tab key="general">General</v-tab>
            <v-tab key="description">Description</v-tab>
            <v-tab key="sitesAffected">Sites Affected</v-tab>
            <v-tab key="remarks">Remarks</v-tab>
            <v-tab key="alarmDetails">Alarm Details</v-tab>
            <v-tab key="options">Options</v-tab>
            <v-tab key="attachments" v-if="isNew || isEdit">Attachments</v-tab>
            <v-tab key="schedule" v-if="isNewRecurring || isEditRecurring">Schedule</v-tab>
            <v-tab key="transferorders" v-if="netsuiteEnabled && (isNew || isEdit)">
                Transfer Orders
                <svg class="ml-2" style="margin-top: 2px;" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48">
                    <path fill="#C74634" fill-rule="evenodd" d="M31.722 11C39.056 11 45 16.818 45 23.995 45 31.182 39.056 37 31.722 37l-15.44-.006C8.948 36.994 3 31.179 3 23.995 3 16.818 8.948 11 16.282 11h15.44zm-.35 4.585H16.628c-4.749 0-8.6 3.765-8.6 8.41 0 4.652 3.851 8.42 8.6 8.42h14.746c4.749 0 8.603-3.768 8.603-8.42 0-4.645-3.854-8.41-8.603-8.41z"/>
                </svg>
            </v-tab>
        </v-tabs>

        <v-divider />

        <v-card-text id="issue-details-content" class="mt-4" :class="{sitebook: showSitebook}">

            <v-form ref="issueForm" class="fill-height" :lazy-validation="true" v-model="issueFormValid">
                <v-tabs-items v-model="tab" class="tab-content-full-height">
                    <v-tab-item key="general">
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field color="#c42111" label="Issue #" v-model="$props.issue.issue_id" disabled v-if="isNew || isEdit || isNewRecurring || isEditRecurring"></v-text-field>
                                <v-text-field color="#c42111" label="Template Name" v-model="newTemplateName" v-if="isNewTemplate || isEditTemplate" :rules="[validation.required(newTemplateName), $store.state.validationFuncs.noDuplicates(newTemplateName, templatesNamesListEdit)]"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <div class="mt-6" style="fontSize: 18px; textAlign: center" v-if="isEdit">
                                    <span>Lifespan: </span>
                                    <span v-if="issueLifespan.d" style="fontWeight: bold" v-text="`${issueLifespan.d} days `" />
                                    <span v-if="issueLifespan.h" style="fontWeight: 500" v-text="`${issueLifespan.h} hours `" />
                                    <span v-if="issueLifespan.m" v-text="`${issueLifespan.m} min `" />
                                    <span v-if="issueLifespan.s" v-text="`${issueLifespan.s} sec `" />
                                </div>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-datetime-picker label="Start of Issue" v-model="$props.issue.issue_start_time" :timeFormat="'HH:mm:ss'" :datePickerProps="dpProps" :timePickerProps="tpProps" :textFieldProps="{rules: [validation.required($props.issue.issue_start_time)]}"></v-datetime-picker>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field color="#c42111" label="Title" v-model="$props.issue.issue_title"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select color="#c42111" label="Service" :items="$props.resources.services" v-model="issueServices" multiple></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select color="#c42111" label="Category" :items="$props.resources.categories" v-model="$props.issue.issue_category"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select color="#c42111" label="Cause" :items="$props.resources.causes" v-model="$props.issue.issue_cause"></v-select>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-select color="#c42111" label="Status" :items="statuses" v-model="$props.issue.issue_status"></v-select>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field color="#c42111" label="Dup Issue #" v-model="$props.issue.issue_duplicate_id" :disabled="$props.issue.issue_status != 'Duplicate'"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select color="#c42111" label="Priority" :items="priorityList" v-model="$props.issue.issue_priority"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select color="#c42111" label="Severity" :items="severityList" v-model="$props.issue.issue_severity"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field color="#c42111" label="Update Due" hint="Every x hour(s). '0' for never." type="number" min="0" persistent-hint v-model="$props.issue.issue_update_due" :rules="[validation.nonNegInteger($props.issue.issue_update_due)]"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-datetime-picker label="Issue Due" v-model="$props.issue.issue_due_time" :timeFormat="'HH:mm:ss'" :datePickerProps="dpProps" :timePickerProps="tpProps" :textFieldProps="{rules: [validation.required($props.issue.issue_due_time)]}"></v-datetime-picker>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete color="#c42111" label="Owner" :items="issueOwnersList" item-value="issueOwnerKey" item-text="issueOwnerName" v-model="$props.issue.issue_owner" />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field color="#c42111" label="Affected" type="number" min="0" v-model="$props.issue.issue_affected"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-select color="#c42111" label="Affected" :items="affectedList" v-model="$props.issue.issue_affected_type"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-datetime-picker label="End of Issue" v-model="$props.issue.issue_solve_time" :timeFormat="'HH:mm:ss'" :datePickerProps="dpProps" :timePickerProps="tpProps" :disabled="solutionDisabled"></v-datetime-picker>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-switch color="#c42111" label="Auto-close at" v-model="autoCloseEnabled"></v-switch>
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-datetime-picker label="Auto-close" v-model="$props.issue.issue_autoclose_time" :timeFormat="'HH:mm:ss'" :datePickerProps="dpProps" :timePickerProps="tpProps" :disabled="!autoCloseEnabled"></v-datetime-picker>
                            </v-col>
                            <v-col cols="12" md="12">
                                <v-autocomplete autocomplete="off" multiple color="#c42111" label="Tags" :items="$props.resources.issue_tags" v-model="$props.issue.issue_tags" item-text="issue_tag_name" item-value="issue_tag_id">
                                    <template v-slot:selection="data">
                                        <v-tooltip bottom color="rgba(97, 97, 97, 1.0)" v-if="data.item.issue_tag_desc">
                                            <template v-slot:activator="{ on }">
                                                <v-chip v-bind="data.attrs" :input-value="data.selected" close @click:close="removeIssueTag(data.item)" v-on="on">
                                                    {{ data.item.issue_tag_name }}
                                                </v-chip>
                                            </template>
                                            <span>{{ data.item.issue_tag_desc }}</span>
                                        </v-tooltip>
                                        <v-chip v-bind="data.attrs" :input-value="data.selected" close @click="data.select" @click:close="removeIssueTag(data.item)" v-else>
                                            {{ data.item.issue_tag_name }}
                                        </v-chip>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <v-tab-item key="description">
                        <v-textarea class="mt-2" rows="15" color="#c42111" label="Description" outlined no-resize v-model="$props.issue.issue_description"></v-textarea>
                        <v-textarea color="#c42111" rows="12" label="Solution" outlined no-resize v-model="$props.issue.issue_solution"></v-textarea>
                    </v-tab-item>

                    <v-tab-item key="sitesAffected">
                        <v-simple-table dark>
                            <template v-slot:default>
                            <thead>
                                <tr>
                                    <th class="text-left">Office</th>
                                    <th class="text-left">Location</th>
                                    <th class="text-left">Site Name</th>
                                    <th class="text-left"></th>
                                    <th class="text-left"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(site, index) in selectedSites" :key="index">
                                    <td class="text-left">{{ site.site_office }}</td>
                                    <td>{{ site.site_location }}</td>
                                    <td>{{ site.site_name }}</td>
                                    <td>
                                        <span class="sitebook-link" @click="getSitebook(site)">Sitebook</span>
                                    </td>
                                    <td class="text-right" width="200px">
                                        <v-btn icon @click="selectedSites.splice(selectedSites.indexOf(site), 1); $store.commit('setIsDirty', true);"><v-icon>mdi-close</v-icon></v-btn>
                                    </td>
                                </tr>
                            </tbody>
                            </template>
                        </v-simple-table>
                        <v-btn class="mt-5" text @click="showAddSiteAffected = true">Add Site</v-btn>
                    </v-tab-item>

                    <v-tab-item key="remarks">
                        <v-textarea class="mt-2" rows="16" color="#c42111" label="Remarks" readonly outlined no-resize :value="formatIssueRemarks()"></v-textarea>
                        <v-btn class="mb-6" color="#c42111" @click="showAddRemark = true">Add Remark</v-btn>
                        <v-btn class="ml-4 mb-6" color="#c42111" @click="showIssueTimeline = true;">Timeline</v-btn>
                        <v-textarea class="mt-4" color="#c42111" rows="16" label="History" readonly outlined no-resize :value="formatIssueHistory()"></v-textarea>
                    </v-tab-item>

                    <v-tab-item key="alarmDetails">
                        <v-simple-table v-if="$props.issue.alarmDetails">
                            <thead><tr>
                                <th class="tex-left">Alarm Time</th>
                                <th class="tex-left">Alarm Local Time</th>
                                <th class="tex-left">Office</th>
                                <th class="tex-left">Location</th>
                                <th class="tex-left">Severity</th>
                                <th class="tex-left">Locale</th>
                                <th class="tex-left">Description</th>
                                <th class="tex-left">Ack Time</th>
                                <th class="tex-left">Acked By</th>
                                <th class="tex-left">Clear Time</th>
                                <th class="tex-left">Cleared By</th>
                                <th class="tex-left">Technician</th>
                                <th class="tex-left">Site Name</th>
                                <th class="tex-left">Site Tags</th>
                            </tr></thead>
                            <tbody><tr>
                                <td>{{ $props.issue.alarmDetails.alarm_time }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_local_time }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_office }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_location }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_severity }}</td>
                                <td>{{ $props.issue.alarmDetails.site_locale }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_description }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_ack_time }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_ack_user }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_clear_time }}</td>
                                <td>{{ $props.issue.alarmDetails.alarm_clear_user }}</td>
                                <td>{{ $props.issue.alarmDetails.user_username }}</td>
                                <td>{{ $props.issue.alarmDetails.site_name }}</td>
                                <td>{{ $props.issue.alarmDetails.site_tags }}</td>
                            </tr></tbody>
                        </v-simple-table>

                        <v-row dense no-gutters>
                            <v-col cols="12" lg="6" dense>
                                <v-textarea rows="15" class="mr-1" color="#c42111" label="History" readonly outlined no-resize v-model="formattedAlarmHistoryText">
                                    <template v-slot:label>
                                        <span style="width: 125px; display: block;">Alarm History 
                                            <v-tooltip bottom color="rgba(97, 97, 97, 1.0)">
                                                <template v-slot:activator="{ on }">
                                                    <v-icon style="margin-top: -4px;" v-on="on">mdi-information-outline</v-icon>
                                                </template>
                                                <span v-html="pageMethodsInfo" />
                                            </v-tooltip>
                                        </span>
                                    </template>
                                </v-textarea>
                            </v-col>
                            <v-col cols="12" lg="6" dense>
                                <v-textarea rows="15" class="ml-1" color="#c42111" label="Alarm Remarks" readonly outlined no-resize v-model="formattedAlarmRemarksText"></v-textarea>
                            </v-col>
                            <v-col cols="12" dense>
                                <v-textarea class="mt-2" rows="10" color="#c42111" label="Full Text" readonly outlined no-resize v-model="$props.issue.alarm_full_text"></v-textarea>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <v-tab-item key="options">
                        <h2>Owner</h2>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify If: Revisions committed" v-model="ownerNotifyRevisionsCommitted"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify If: Due deadline passed" v-model="ownerNotifyDeadlinePassed"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify Methods: Send to pager" v-model="ownerMethodsPager"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify Methods: Client notification" v-model="ownerMethodsClientNotify"></v-checkbox>
                            </v-col>
                        </v-row>
                        <h2 class="mt-8 mb-3">Interested Parties</h2>
                        <v-row dense>
                            <v-col cols="12">
                                <v-select color="#c42111" label="Members" :items="issueOwnersList" item-value="issueOwnerKey" item-text="issueOwnerName" multiple v-model="interestedMembers"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify If: Revisions committed" v-model="membersNotifyRevisionsCommitted"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify If: Due deadline passed" v-model="membersNotifyDeadlinePassed"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify Methods: Send to pager" v-model="membersMethodsPager"></v-checkbox>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-checkbox color="#c42111" label="Notify Methods: Client notification" v-model="membersMethodsClientNotify"></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <v-tab-item key="attachments" v-if="isNew || isEdit">
                        <v-simple-table dark>
                            <template v-slot:default>
                            <thead>
                                <tr>
                                    <th class="text-left">Name</th>
                                    <th class="text-left">Type</th>
                                    <th class="text-left">Size</th>
                                    <th class="text-left"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(attachment, index) in attachments" :key="index">
                                    <td class="text-left">{{ attachment.name }}</td>
                                    <td>{{ attachment.type }}</td>
                                    <td>{{ attachment.size ? getReadableFileSize(attachment.size) : '' }}</td>
                                    <td class="text-right" width="200px">
                                        <v-btn icon @click="downloadAttachment(attachment.name)" v-if="!attachment.uploading"><v-icon>mdi-cloud-download</v-icon></v-btn>
                                        <v-btn icon @click="deleteAttachmentClick(attachment.name)" v-if="!attachment.uploading" :disabled="lockSave"><v-icon>mdi-delete</v-icon></v-btn>
                                        <v-progress-linear v-if="attachment.uploading && attachment.uploadProgress != 100 && attachment.uploadProgress != -1" :value="attachment.uploadProgress" color="#c42111" />
                                        <span v-if="attachment.uploading && attachment.uploadProgress == 100">processing <v-icon>fas fa-spinner fa-spin</v-icon></span>
                                        <span v-if="attachment.uploading && attachment.uploadProgress == -1">Files will be uploaded when issue is created.</span>
                                    </td>
                                </tr>
                            </tbody>
                            </template>
                        </v-simple-table>

                        <v-divider />

                        <v-btn class="mt-5" text @click="showIssueFileUpload = true" :disabled="lockSave">Add New</v-btn>
                    </v-tab-item>

                    <v-tab-item key="schedule" v-if="isNewRecurring || isEditRecurring" eager>
                        <schedule-details ref="scheduleDetails" :issue="issue"></schedule-details>
                    </v-tab-item>

                    <v-tab-item key="transferorders" v-if="netsuiteEnabled && (isNew || isEdit)">
                        <transfer-orders :issue_id="$props.issue.issue_id" :newIssue="isNew" @saveIssue="saveChanges(true)" />
                    </v-tab-item>
                    
                </v-tabs-items>
            </v-form>

        </v-card-text>

        <v-card-text id="sitebook-content" class="" :class="{sitebook: showSitebook}">
            <div class="sitebook-content-container">
                <issue-site-book :sitebook="sitebook" />
            </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn text @click="okClick" :disabled="lockSave">Ok</v-btn>
            <v-btn text @click="closeClick">Close</v-btn>
        </v-card-actions>


        <v-dialog v-model="showAddSiteAffected" max-width="80%" width="700px" persistent>
            <add-site-affected v-if="showAddSiteAffected" @cancel="showAddSiteAffected = false" @ok="addSites" :sitesList="$props.resources.sites"></add-site-affected>
        </v-dialog>

        <v-dialog v-model="showAddRemark" max-width="80%" width="500px" persistent>
            <add-remark v-if="showAddRemark" @cancel="showAddRemark = false" @ok="addRemark"></add-remark>
        </v-dialog>

        <v-dialog v-model="showIssueFileUpload" max-width="80%" width="700px" persistent>
            <issue-file-upload v-if="showIssueFileUpload" :existingAttachments="attachments" @cancel="showIssueFileUpload = false" @save="saveAttachmentsClick"></issue-file-upload>
        </v-dialog>

        <v-dialog v-model="showIssueTimeline" max-width="80%" width="1200px" persistent v-if="showIssueTimeline" @keydown="(k) => {k.key == 'Escape' ? showIssueTimeline = false : null}">
            <issue-timeline :remarks="$props.issue.issue_remarks" :history="$props.issue.issue_history" @close="showIssueTimeline = false" />
        </v-dialog>

    </v-card>
</template>

<script>
import AddSiteAffected from "@/components/Issues/AddSiteAffected";
import AddRemark from "@/components/Issues/AddRemark";
import IssueFileUpload from "@/components/Issues/IssueFileUpload";
import IssueSiteBook from "@/components/Issues/IssueSiteBook";
import IssueTimeline from "@/components/Issues/IssueTimeline";
import ScheduleDetails from "@/components/Issues/ScheduleDetails";
import TransferOrders from "@/components/Integrations/Netsuite/TransferOrders";

export default {
    name: 'IssueDetails',
    props: ['issue', 'type', 'resources', 'templateName', 'templatesNamesList'],
    components: {
        AddSiteAffected,
        AddRemark,
        IssueFileUpload,
        IssueSiteBook,
        IssueTimeline,
        ScheduleDetails,
        TransferOrders
    },
    data: () => ({
        tab: 0,
        apiHost: TELSA.apiHost,
        lockSave: false,
        issueFormValid: false,
        originalIssue: null,
        tpProps: {
            useSeconds: true,
            format: '24hr',
            color: '#c42111',
        },
        dpProps: {
            color: '#c42111',
        },
        //statuses: ['Open', 'In Progress', 'Monitoring', 'Need Update', 'Delinquent', 'Completed', 'Closed', 'Duplicate'],

        statuses: [
            {text: 'Open', value: 'Open', disabled: false},
            {text: 'In Progress', value: 'In Progress', disabled: false},
            {text: 'Monitoring', value: 'Monitoring', disabled: false},
            {text: 'Need Update', value: 'Need Update', disabled: false},
            {text: 'Delinquent', value: 'Delinquent', disabled: false},
            {text: 'Completed', value: 'Completed', disabled: false},
            {text: 'Closed', value: 'Closed', disabled: false},
            {text: 'Duplicate', value: 'Duplicate', disabled: false}
        ],
        

        issueOwnersList: [],
        autoCloseEnabled: false,
        affectedList: ['DS0s', 'DS1s', 'DS3s', 'Users'],
        priorityList: ['Highest', 'High', 'Normal', 'Low'],
        severityList: ['CRITICAL', 'MAJOR', 'MINOR', 'OBSERVATIONAL'],
        issueLifespan: null,

        selectedSites: [],
        showAddSiteAffected: false,

        showAddRemark: false,

        ownerNotifyRevisionsCommitted: false,
        ownerNotifyDeadlinePassed: false,
        ownerMethodsPager: false,
        ownerMethodsClientNotify: false,
        membersNotifyRevisionsCommitted: false,
        membersNotifyDeadlinePassed: false,
        membersMethodsPager: false,
        membersMethodsClientNotify: false,

        showIssueFileUpload: false,
        attachments: [],

        oldTemplateName: null,
        newTemplateName: null,
        validation: {
            required: (value) => (!!value || `Value required`),
            nonNegInteger: (value) => (value !== "") && ((value.toString().indexOf('.') === -1) && (value.toString().indexOf('e') === -1) && (value.toString().indexOf('-') === -1) && parseInt(Number(value)) > -1) || `Value must be a non-negative integer`,
        },

        sitebook: null,
        showSitebook: false,
        showIssueTimeline: false
    }),

    computed: {
        isEdit: function() {
            return this.$props.type == 'edit';
        },
        isNew: function() {
            return this.$props.type == 'new';
        },
        isEditTemplate: function() {
            return this.$props.type == 'edit_template';
        },
        isNewTemplate: function() {
            return this.$props.type == 'new_template';
        },
        isEditRecurring: function() {
            return this.$props.type == 'edit_recurring';
        },
        isNewRecurring: function() {
            return this.$props.type == 'new_recurring';
        },
        
        issueServices: {
            get: function() {
                let arr = this.$props.issue.issue_services.split(';');
                if(arr.length == 1 && arr[0] == "") {
                    arr = [];
                }
                return arr;
            },
            set: function(value) {
                this.$props.issue.issue_services = value.join(';');
            }
        },

        interestedMembers: {
            get: function() {
                return this.$props.issue.issue_ip_members.split(';');
            },
            set: function(value) {
                this.$props.issue.issue_ip_members = value.join(';');
            }
        },

        solutionDisabled: function() {
            let status = this.$props.issue.issue_status;
            if(status == 'Open' || status == 'In Progress' || status == 'Monitoring') {
                return true;
            }
            return false;
        },

        templatesNamesListEdit() {
            // They're editing an existing Issue Template so remove its name from the list.
            if(this.isEditTemplate) {
                return this.$props.templatesNamesList.filter(x => x != this.$props.templateName)
            }
            return this.$props.templatesNamesList;
        },

        netsuiteEnabled: function() {
            return this.$store.state.batchConfig.webapp_netsuite;
        },

        formattedAlarmHistoryText() {
            if(this.$props.issue.alarmDetails && this.$props.issue.alarmDetails.alarm_history && this.$props.issue.alarmDetails.alarm_history.length) {
                return this.$props.issue.alarmDetails.alarm_history.join('\n');
            }
            return "";
        },

        formattedAlarmRemarksText() {
            if(this.$props.issue.alarmDetails && this.$props.issue.alarmDetails.alarm_remarks && this.$props.issue.alarmDetails.alarm_remarks.length) {
                return this.$props.issue.alarmDetails.alarm_remarks.join('\n');
            }
            return "";
        },

        pageMethodsInfo() {
            return `<u>Page Methods</u><br>
            <b>M</b> - Email<br>
            <b>S</b> - SMS<br>
            <b>T</b> - Telegram<br>
            <b>W</b> - Whatsapp<br>
            <b>H</b> - Microsoft Teams<br>
            <b>C</b> - Webapp Pop-Up<br>
            <b>A</b> - Audio<br>
            <b>P</b> - Push Notification<br>`;
        }
    },

    methods: {
        createIssueOwnersList: function() {
            let users = JSON.parse(JSON.stringify(this.$store.state.batchConfig.users));
            let groups = JSON.parse(JSON.stringify(this.$store.state.batchConfig.groups));
            let list = [];
            for(let user of users) {
                user.issueOwnerName = `USER: ${user.user_fullname}`;
                user.issueOwnerKey = user.user_username;
                list.push(user);
            }
            for(let group of groups) {
                group.issueOwnerName = `GROUP: ${group.group_name}`;
                group.issueOwnerKey = group.group_name;
                list.push(group);
            }
            if(this.isEditTemplate || this.isNewTemplate) {
                list.push({issueOwnerName: 'ALARM SITE TECHNICIAN', issueOwnerKey: 'alarm_site_technician'});
            }
            this.issueOwnersList = list;
        },

        createSelectedSitesList: function() {
            for(let selectedSiteId of this.$props.issue.site_id) {
                this.selectedSites.push(this.$props.resources.sites.find(site => {
                    return site.site_id == selectedSiteId;
                }));
            }
        },

        calculateIssueLifespan: function() {
            let start = TELSA.moment.tz(this.$props.issue.issue_start_time, this.$store.state.batchConfig.timezone);
            let now = TELSA.moment.tz(this.$store.state.batchConfig.timezone);
            let end = this.$props.issue.issue_close_time ? TELSA.moment.tz(this.$props.issue.issue_close_time, this.$store.state.batchConfig.timezone) : now;
            let diff = end.diff(start);
            let duration = TELSA.convertMilisecondsToReadableDuration(diff, 'x');
            this.issueLifespan = duration;
        },

        addSites: function(newSites) {
            for(let newSite of newSites) {
                if(!this.selectedSites.some(site => {
                    return (site.site_id == newSite.site_id)
                })) {
                    this.selectedSites.push(newSite);
                    this.$store.commit('setIsDirty', true);
                }
            }
            this.showAddSiteAffected = false;
        },

        addRemark: function(remark) {
            let username = this.$store.state.user.user_username;
            let ts = TELSA.moment.tz(this.$store.state.batchConfig.timezone).format('YYYY-MM-DD HH:mm:ss');
            this.$props.issue.issue_remarks.push({username: username, issue_action_timestamp: ts, issue_action_details: remark, issue_action: 'remark'})
            this.showAddRemark = false;
        },

        setOptions: function() {
            let ownerVal1 = this.$props.issue.issue_options.split(';')[0];
            let ownerVal2 = this.$props.issue.issue_options.split(';')[1];
            let membersVal1 = this.$props.issue.issue_ip_options.split(';')[0];
            let membersVal2 = this.$props.issue.issue_ip_options.split(';')[1];

            this.ownerNotifyRevisionsCommitted = (ownerVal1 == 1 || ownerVal1 == 3);
            this.ownerNotifyDeadlinePassed = (ownerVal1 == 2 || ownerVal1 == 3);
            this.ownerMethodsPager = (ownerVal2 == 1 || ownerVal2 == 3);
            this.ownerMethodsClientNotify = (ownerVal2 == 2 || ownerVal2 == 3);
            this.membersNotifyRevisionsCommitted = (membersVal1 == 1 || membersVal1 == 3);
            this.membersNotifyDeadlinePassed = (membersVal1 == 2 || membersVal1 == 3);
            this.membersMethodsPager = (membersVal2 == 1 || membersVal2 == 3);
            this.membersMethodsClientNotify = (membersVal2 == 2 || membersVal2 == 3);
        },

        initializeAttachmentsList: function() {
            if(this.$props.issue.files) {
                for(let file of this.$props.issue.files) {
                    this.attachments.push({
                        name: file,
                        type: file.split('.')[file.split('.').length - 1],
                        size: '',
                        uploading: false,
                        uploadProgress: 100
                    });
                }
            }
        },

        saveAttachmentsClick: function(attachments) {
            for(let attachment of attachments) {
                let attachmentObj = {name: attachment.name, type: attachment.type, size: attachment.size, uploading: true, uploadProgress: (this.isNew ? -1 : 0), file: attachment}
                this.attachments.push(attachmentObj);
            }

            this.showIssueFileUpload = false;
            if(!this.isNew) {
                this.saveAttachments();
            }
        },

        saveAttachments: function() {
            let route = `${TELSA.apiHost}issues/files/upload`;
            for(let attachment of this.attachments) {
                TELSA.fileToBase64(attachment.file).then(data => {
                    this.$http.post(route, {args: {fileData: data, fileName: attachment.name, issueId: this.$props.issue.issue_id}},
                    {progress(e) {
                        let progress = Math.floor((e.loaded / e.total) * 100);
                        attachment.uploadProgress = progress;
                    }}).then(response => {
                        let result = response.body;
                        attachment.uploading = false;
                    });
                });
            }
        },

        getReadableFileSize: function(size) {
            return TELSA.readableFileSize(size);
        },

        downloadAttachment: function(attachmentName) {
            TELSA.loading(true);
            let route = `${TELSA.apiHost}issues/files/download`;
            return this.$http.post(route, {args: {fileName: attachmentName, issueId: this.$props.issue.issue_id}}).then(response => {
                let fileData = response.body;
                TELSA.loading(false);
                const url = `data:application/octet-stream;base64,${fileData}`;
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = attachmentName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            });
        },

        deleteAttachmentClick: function(attachmentName) {
            this.$store.commit('showConfirmModal', {
                title: 'Delete Issue Attachment',
                message: `This operation will permanently delete <b>${attachmentName}</b> from this issue. Are you sure you want to do this?`,
                type: 'delete',
                hideCancel: false,
                confirmAction: () => this.deleteAttachment(attachmentName)
            });
        },
        deleteAttachment: function(attachmentName) {
            TELSA.loading(true);
            for(let [index, attachment] of this.attachments.entries()) {
                if(attachment.name == attachmentName) {
                    this.attachments.splice(index, 1);
                    break;
                }
            }
            let route = `${TELSA.apiHost}issues/files/delete`;
            return this.$http.post(route, {args: {fileName: attachmentName, issueId: this.$props.issue.issue_id}}).then(response => {
                let result = response.body;
                TELSA.loading(false);
            });
        },

        okClick: function() {
            if(this.$refs.issueForm.validate()) {
                if(this.isNewRecurring || this.isEditRecurring) {
                    // Call this to get the formatting. 
                    this.saveChanges();
                    return;
                }

                let type = 'issue';
                if(this.isNewTemplate || this.isEditTemplate) {
                    type = 'template';
                }
                else if(this.isNewRecurring || this.isEditRecurring) {
                    type = 'recurring issue';
                }
                this.$store.commit('showConfirmModal', {
                    title: 'Are you sure?',
                    message: `Are you sure you want to commit changes to this ${type}?`,
                    type: '',
                    hideCancel: false,
                    confirmAction: () => this.saveChanges()
                });
            }
        },
        saveChanges: function() {
            // format/apply changes
            this.$props.issue.issue_options = `${this.convertOptionsToText(this.ownerNotifyRevisionsCommitted, this.ownerNotifyDeadlinePassed)};${this.convertOptionsToText(this.ownerMethodsPager, this.ownerMethodsClientNotify)}`;
            this.$props.issue.issue_ip_options = `${this.convertOptionsToText(this.membersNotifyRevisionsCommitted, this.membersNotifyDeadlinePassed)};${this.convertOptionsToText(this.membersMethodsPager, this.membersMethodsClientNotify)}`;
            this.$props.issue.issue_duplicate_id = this.$props.issue.issue_duplicate_id ? this.$props.issue.issue_duplicate_id : 0;
            this.$props.issue.site_id = [];
            this.$props.issue.site_name = [];
            for(let site of this.selectedSites) {
                this.$props.issue.site_id.push(site.site_id);
                this.$props.issue.site_name.push(site.site_name);
                this.$props.issue.site_office.push(site.site_office);
                this.$props.issue.site_location.push(site.site_location);
            }

            // Format all UI times
            this.$props.issue.issue_start_time = this.$props.issue.issue_start_time ? TELSA.moment(this.$props.issue.issue_start_time).format('YYYY-MM-DD HH:mm:ss') : "";
            this.$props.issue.issue_due_time = this.$props.issue.issue_due_time ? TELSA.moment(this.$props.issue.issue_due_time).format('YYYY-MM-DD HH:mm:ss') : "";
            this.$props.issue.issue_solve_time = this.$props.issue.issue_solve_time ? TELSA.moment(this.$props.issue.issue_solve_time).format('YYYY-MM-DD HH:mm:ss') : "";
            this.$props.issue.issue_autoclose_time = this.$props.issue.issue_autoclose_time ? TELSA.moment(this.$props.issue.issue_autoclose_time).format('YYYY-MM-DD HH:mm:ss') : "";


            let username = this.$store.state.user.user_username;
            let ts = TELSA.moment.tz(this.$store.state.batchConfig.timezone).format('YYYY-MM-DD HH:mm:ss');
            if(this.isNew || this.isNewRecurring) {
                // add 'new' message to history and set issue open time
                let fromTemplate = this.$props.templateName ? ` from template: ${this.$props.templateName}` : "";

                //let historyText = `\n[${username} at ${ts}] created issue${fromTemplate}.`;
                this.$props.issue.issue_history.push({user: username, ts: ts, issue_action_details: `created issue${fromTemplate}.`});

                //this.$props.issue.issue_history = this.$props.issue.issue_history + historyText;
                this.$props.issue.issue_open_time = ts;
            }
            else if(this.isNewTemplate) {
                // add 'new' message to history and set issue open time

                //let historyText = `\n[${username} at ${ts}] created issue template.`;
                this.$props.issue.issue_history.push({user: username, ts: ts, issue_action_details: `created issue template.`});

                //this.$props.issue.issue_history = this.$props.issue.issue_history + historyText;
                this.$props.issue.issue_open_time = ts;
            }
            else if(this.isEdit || this.isEditTemplate) {
                //this.$props.issue.issue_history = this.annotateChanges() + this.$props.issue.issue_history;
                this.annotateChanges();
            }

            if(!this.autoCloseEnabled) {
                // If they didn't enable auto-close then we dont want to save a value for it
                this.$props.issue.issue_autoclose_time = "";
            }

            if(this.$props.issue.issue_status == 'Closed') {
                // add 'closed' message to history and set issue close time
                this.$props.issue.issue_close_time = ts;
                this.$props.issue.issue_close_user = this.$store.state.user.user_username;
            }
            else {
                this.$props.issue.issue_close_user = null;
            }
            this.$props.issue.issue_revised_time = ts;

            if(this.isNew || this.isEdit) {
                TELSA.loading(true);
                let route = `${TELSA.apiHost}issues/save`;
                return this.$http.post(route, {args: {is_new: this.isNew, lock: (this.isNew ? null : this.$store.state.lock), issue: this.$props.issue}}).then(response => {
                    let result = response.body;
                    this.$store.commit('setIsDirty', false);
                    this.$store.dispatch('handleRemoveLocks');
                    TELSA.loading(false);
                    if(this.isNew) {
                        this.saveAttachments();
                    }
                    this.$emit('close');
                });
            }
            else if(this.isNewTemplate) {
                TELSA.loading(true);
                let route = `${TELSA.apiHost}issues/templates/add`;
                let template = {issue_template_name: this.newTemplateName, issue_template: this.$props.issue};
                return this.$http.post(route, {args: {lock: this.$store.state.lock, template: template}}).then(response => {
                    let result = response.body;
                    this.$store.commit('setIsDirty', false);
                    TELSA.loading(false);
                    this.$emit('saved', template);
                });
            }
            else if(this.isEditTemplate) {
                TELSA.loading(true);
                let route = `${TELSA.apiHost}issues/templates/edit`;
                let template = {issue_template_name: this.newTemplateName, issue_template: this.$props.issue};
                return this.$http.post(route, {args: {lock: this.$store.state.lock, oldTemplateName: this.oldTemplateName, template: template}}).then(response => {
                    let result = response.body;
                    this.$store.commit('setIsDirty', false);
                    TELSA.loading(false);
                    this.$emit('saved', template);
                });
            }
            else if(this.isNewRecurring || this.isEditRecurring) {
                if(this.$refs.scheduleDetails.validate()) {
                    TELSA.loading(true);
                    let route = `${TELSA.apiHost}issues/recurring/save`;
                    return this.$http.post(route, {args: {lock: this.$store.state.lock, is_new: this.isNewRecurring, issue: this.$props.issue}}).then(response => {
                        let result = response.body;
                        this.$store.dispatch('handleRemoveLocks');
                        this.$store.commit('setIsDirty', false);
                        TELSA.loading(false);
                        this.$emit('save');
                    });
                }
            }


            // else if(this.isNewRecurring || this.isEditRecurring) {
            //     this.$store.commit('setIsDirty', false);
            //     this.$emit('ok');
            // }
        },

        annotateChanges: function() {
            let oldIssue = this.originalIssue;
            let newIssue = this.$props.issue;
            let message = '';
            let username = this.$store.state.user.user_username;
            let ts = TELSA.moment.tz(this.$store.state.batchConfig.timezone).format('YYYY-MM-DD HH:mm:ss');

            let addToMessage = function(name, oldVal, newVal) {
                let action = `changed ${name} from "${oldVal}" to "${newVal}" \n`;
                message += action;
            };

            // console.log('old issue', oldIssue);
            // console.log('new issue', newIssue);

            //issue_affected:0
            if(oldIssue.issue_affected != newIssue.issue_affected) {addToMessage('Affected', oldIssue.issue_affected, newIssue.issue_affected);}
            if(oldIssue.issue_affected_type != newIssue.issue_affected_type) {addToMessage('Affected Type', oldIssue.issue_affected_type, newIssue.issue_affected_type);}
            if(oldIssue.issue_autoclose_time != newIssue.issue_autoclose_time) {addToMessage('Issue Auto-close Time', oldIssue.issue_autoclose_time, newIssue.issue_autoclose_time);}
            if(oldIssue.issue_category != newIssue.issue_category) {addToMessage('Category', oldIssue.issue_category, newIssue.issue_category);}
            if(oldIssue.issue_cause != newIssue.issue_cause) {addToMessage('Cause', oldIssue.issue_cause, newIssue.issue_cause);}
            if(oldIssue.issue_description != newIssue.issue_description) {addToMessage('Description', oldIssue.issue_description, newIssue.issue_description);}
            if(oldIssue.issue_due_time != newIssue.issue_due_time) {addToMessage('Issue Due Time', oldIssue.issue_due_time, newIssue.issue_due_time);}
            if(oldIssue.issue_duplicate_id != newIssue.issue_duplicate_id) {addToMessage('Dup Issue #', oldIssue.issue_duplicate_id, newIssue.issue_duplicate_id);}
            if(oldIssue.issue_fcc_status != newIssue.issue_fcc_status) {addToMessage('FCC Status', oldIssue.issue_fcc_status, newIssue.issue_fcc_status);}
            if(oldIssue.issue_ip_members != newIssue.issue_ip_members) {addToMessage('Interested Parties', oldIssue.issue_ip_members, newIssue.issue_ip_members);}
            if(oldIssue.issue_owner != newIssue.issue_owner) {addToMessage('Owner', oldIssue.issue_owner, newIssue.issue_owner);}
            if(oldIssue.issue_priority != newIssue.issue_priority) {addToMessage('Priority', oldIssue.issue_priority, newIssue.issue_priority);}
            if(oldIssue.issue_services != newIssue.issue_services) {addToMessage('Services', oldIssue.issue_services, newIssue.issue_services);}
            if(oldIssue.issue_severity != newIssue.issue_severity) {addToMessage('Severity', oldIssue.issue_severity, newIssue.issue_severity);}
            if(oldIssue.issue_solution != newIssue.issue_solution) {addToMessage('Solution', oldIssue.issue_solution, newIssue.issue_solution);}
            if(oldIssue.issue_solve_time != newIssue.issue_solve_time) {addToMessage('Issue Solve Time', oldIssue.issue_solve_time, newIssue.issue_solve_time);}
            if(oldIssue.issue_start_time != newIssue.issue_start_time) {addToMessage('Issue Start Time', oldIssue.issue_start_time, newIssue.issue_start_time);}
            if(oldIssue.issue_status != newIssue.issue_status) {addToMessage('Status', oldIssue.issue_status, newIssue.issue_status);}
            if(oldIssue.issue_title != newIssue.issue_title) {addToMessage('Title', oldIssue.issue_title, newIssue.issue_title);}
            if(oldIssue.issue_update_due != newIssue.issue_update_due) {addToMessage('Update Due Time', oldIssue.issue_update_due, newIssue.issue_update_due);}

            // Sites. TODO: get more specific with the changes
            if(!TELSA.arrayEqualCheck(oldIssue.site_id, newIssue.site_id)) {
                message += `Changed Affected Site(s) \n`; 
                //this.$props.issue.issue_history.push({user: username, ts: ts, issue_action_details: `Changed Affected Site(s) \n`});
            }

            // Remarks. TODO: get more specific with the changes
            console.log('oldIssue: ', oldIssue);
            console.log('newIssue: ', newIssue);
            // if(oldIssue.issue_remarks != newIssue.issue_remarks) {
            //     //message += `Added remark(s) \n`; 

            //     this.$props.issue.journal.push({user: username, ts: ts, action: `remark`});
            // }

            // Options. TODO: get more specific with the changes
            if(oldIssue.issue_options.split(';')[0] != newIssue.issue_options.split(';')[0]) {message += `Changed Owner Notify if options \n`;}
            if(oldIssue.issue_options.split(';')[1] != newIssue.issue_options.split(';')[1]) {message += `Changed Owner Notify methods options \n`;}
            if(oldIssue.issue_ip_options.split(';')[0] != newIssue.issue_ip_options.split(';')[0]) {message += `Changed Interested Parties Notify if options \n`;}
            if(oldIssue.issue_ip_options.split(';')[1] != newIssue.issue_ip_options.split(';')[1]) {message += `Changed Interested Parties Notify methods options \n`;}

            if(message != '') {
                //message = `[${username} at ${ts}] ${message}`;
                message = message.replace(/\n$/, ""); // remove last \n
                newIssue.issue_history.push({user: username, ts: ts, issue_action_details: message});
            }
            console.log('CHANGES', message);
            //return message;
        },

        closeClick: function() {
            if(this.$store.state.isDirty) {
                this.$store.commit('showConfirmModal', {
                    title: 'Are you sure?',
                    message: `Are you sure you want to discard changes to this issue?`,
                    type: 'warning',
                    hideCancel: false,
                    confirmAction: () => {
                        this.$store.commit('setIsDirty', false);
                        this.close();
                    }
                });
            }
            else {
                this.close();
            }
        },

        close: function() {
            if(this.isNew || this.isEdit) {
                this.$store.dispatch('handleRemoveLocks');
            }
            this.$emit('close');
        },

        convertOptionsToText: function(value1, value2) {
            if(value1 && !value2) {
                return 1;
            }
            else if(!value1 && value2) {
                return 2;
            }
            else if(value1 && value2) {
                return 3;
            }
            return 0;
        },

        getSitebook: function(site) {
            TELSA.loading(true);
            let route = `${TELSA.apiHost}sitebook/tree`;
            return this.$http.post(route, {args: {site: site}}).then(response => {
                let result = response.body;
                console.log('result: ', result);
                if(result) {
                    // There exists a sitebook so lets do some magic!
                    result.site = site;
                    this.sitebook = result;
                    this.$store.commit('setEditSiteBookFromAlarmShortcut', site);
                    this.showSitebook = true;
                }
                else {
                    this.$store.commit('showConfirmModal', {
                        title: 'Site Book Help',
                        message: `There is no site book set up for this selected site. You can create a site book from the <b>Site Book</b> page`,
                        type: '',
                        hideCancel: true,
                        confirmAction: () => {}
                    });
                }
                TELSA.loading(false);
            });
        },

        removeIssueTag: function(item) {
            const index = this.$props.issue.issue_tags.indexOf(item.issue_tag_id);
            if(index >= 0) {
                this.$props.issue.issue_tags.splice(index, 1);
            }
        },

        formatIssueRemarks: function() {
            let formattedRemarks = ``;
            this.$props.issue.issue_remarks.forEach(journal => {
                formattedRemarks += `[${journal.username} at ${journal.issue_action_timestamp}] ${journal.issue_action_details}\n`;
            });
            return formattedRemarks;
        },

        formatIssueHistory: function() {
            let formattedHistory = ``;
            this.$props.issue.issue_history.forEach(journal => {
                formattedHistory += `[${journal.username} at ${journal.issue_action_timestamp}] ${journal.issue_action_details}\n`;
            });
            return formattedHistory;
        }
    },

    watch: {
        issue: {
            handler(newVal, oldVal) {
                if(!this.lockSave) {
                    this.$store.commit('setIsDirty', true);
                }
            },
            deep: true
        }
    },

    mounted() {
        if(this.isEdit) {
            // Permissions Check
            let permission = TELSA.checkPermission(this.$store.state.user, 'issue/revise');
            if(!permission) {
                let self = this;
                this.$store.commit('insufficientPermissions', {});
                this.lockSave = true;
                return;
            }

            if(!TELSA.checkPermission(this.$store.state.user, 'issue/close')) {
                this.statuses.find(status => {
                    return status.text == 'Closed';
                }).disabled = true;
            }

            let lockName = `edit_issue/${this.$props.issue.issue_id}`;
            this.$store.dispatch('getLock', lockName).then(lockOwner => {
                if(lockOwner) {
                    // Disable save and let user know page is locked!
                    this.lockSave = true;
                    this.$store.commit('showConfirmModal', {
                        title: 'Lock Warning',
                        message: `The Issue is already locked for editing by <font color="#FB8C00"><b>${lockOwner}</b></font>. You cannot edit it at this time.`,
                        type: 'warning',
                        hideCancel: true,
                        confirmAction: function(){}
                    });
                }
                TELSA.loading(false);
            });
        }
        // else if(this.isEditRecurring) {
        //     let lockName = `edit_issue/${this.$props.issue.issue_id}`;
        //     this.$store.dispatch('getLock', lockName).then(lockOwner => {
        //         if(lockOwner) {
        //             // Disable save and let user know page is locked!
        //             this.lockSave = true;
        //             this.$store.commit('showConfirmModal', {
        //                 title: 'Lock Warning',
        //                 message: `The Recurring Issue is already locked for editing by <font color="#FB8C00"><b>${lockOwner}</b></font>. You cannot edit it at this time.`,
        //                 type: 'warning',
        //                 hideCancel: true,
        //                 confirmAction: function(){}
        //             });
        //         }
        //         TELSA.loading(false);
        //     });
        // }

        if(this.isEdit || this.isEditTemplate) {
            // Clone a copy of the original issue so that when we save it we can annotate what the changes are. 
            this.originalIssue = JSON.parse(JSON.stringify(this.$props.issue));
        }

        if(this.isNewTemplate || this.isEditTemplate) {
            this.newTemplateName = this.$props.templateName;
            this.oldTemplateName = this.$props.templateName;
        }

        if(this.$props.issue && this.$props.issue.issue_update_due === "") {
            this.$props.issue.issue_update_due = 0;
        }
    },

    created() {
        this.createIssueOwnersList();
        this.autoCloseEnabled = !!this.$props.issue.issue_autoclose_time;
        this.createSelectedSitesList();
        this.setOptions();
        this.calculateIssueLifespan();
        this.initializeAttachmentsList();
    }

  
};
</script>

<style scoped>

    #issue-details {
        height: 80vh;
        max-height: 80vh;
    }

    #issue-details-content {
        height: calc(100% - 177px);
        overflow: auto;
    }
    #issue-details-content.sitebook {
        display: none;
    }

    .sitebook-link {
        cursor: pointer;
        text-decoration: underline;
        color: #2266ff;
    }

    #sitebook-content {
        display: none;
        height: calc(100% - 116px);
        overflow: auto;
    }
    #sitebook-content.sitebook {
        display: block;
    }

    #sitebook-content .sitebook-content-container {
        display: block;
        position: relative;
        height: 100%;
        overflow: auto;

    }

</style>